// libraryRowIntent — the Library rows' `intent` surface (ui-as-projection.md):
// which lifecycle actions a row offers (a projection of its state), UIEvent →
// a typed intent, and intent → the ONE host handler that runs the matching app
// command. Each intent is its own named variant (distinct command per intent,
// never a toggle flag); the host supplies the handlers, so the surface stays a
// pure projection with no I/O of its own.

import type { ResourceRef } from "../Navigation/types.js";
import type { SearchResult } from "./searchModel.js";

// Retitle a resource (core `RenameResource`).
type RenameResourceIntent = {
    readonly type: "rename-resource";
    readonly ref: ResourceRef;
    readonly title: string;
};
// Set a resource aside (core `ArchiveResource`, F86).
type ArchiveResourceIntent = {
    readonly type: "archive-resource";
    readonly ref: ResourceRef;
};
// Bring an archived resource back to the default view (core
// `UnarchiveResource`, F86). Distinct from restore-after-delete.
type UnarchiveResourceIntent = {
    readonly type: "unarchive-resource";
    readonly ref: ResourceRef;
};
// Ask to delete a resource: opens the shared delete confirmation, whose
// confirm runs `DeleteResource` (with undo). Never deletes on its own.
type RequestDeleteResourceIntent = {
    readonly type: "request-delete-resource";
    readonly ref: ResourceRef;
};

export type LibraryRowIntent =
    | RenameResourceIntent
    | ArchiveResourceIntent
    | UnarchiveResourceIntent
    | RequestDeleteResourceIntent;

// The host's command runners, one per intent.
export type LibraryRowHandlers = {
    readonly renameResource: (id: string, title: string) => void;
    readonly archiveResource: (ref: ResourceRef) => void;
    readonly unarchiveResource: (ref: ResourceRef) => void;
    readonly requestDeleteResource: (ref: ResourceRef) => void;
};

export const runLibraryRowIntent =
    (handlers: LibraryRowHandlers) =>
    (intent: LibraryRowIntent): void => {
        switch (intent.type) {
            case "rename-resource":
                handlers.renameResource(intent.ref.id, intent.title);
                return;
            case "archive-resource":
                handlers.archiveResource(intent.ref);
                return;
            case "unarchive-resource":
                handlers.unarchiveResource(intent.ref);
                return;
            case "request-delete-resource":
                handlers.requestDeleteResource(intent.ref);
                return;
        }
    };

// The resource a row stands for, or null for a conversation (not a resource).
// Built from the row itself, so an archived row (absent from the active
// library index) still resolves.
export const refOfResult = (result: SearchResult): ResourceRef | null => {
    switch (result.kind) {
        case "source":
            return { type: "source", id: result.id, title: result.title };
        case "material":
            return { type: "material", id: result.id, title: result.title };
        case "package":
            return { type: "export", id: result.id, title: result.title };
        case "file":
            return { type: "file", id: result.id, title: result.title };
        case "conversation":
            return null;
    }
};

// === The row menu ===========================================================

// One entry in a row's overflow menu. "rename" starts the inline field (its
// intent fires on submit, with the new title); the others are one intent each.
export type LibraryRowAction = "rename" | "archive" | "unarchive" | "delete";

// A row's menu, projected from its state: the archive verb follows the row's
// lifecycle (Archive on an active row, Unarchive on an archived one); delete
// is always offered (removal is first-class), last. Every resource kind gets
// the same menu; a row with no ResourceRef (a conversation) gets none.
export const rowActionsFor = (
    result: SearchResult,
): readonly LibraryRowAction[] =>
    refOfResult(result) === null
        ? []
        : [
              "rename",
              result.archived === true ? "unarchive" : "archive",
              "delete",
          ];

// The intent a one-step menu action fires for `ref`.
export const intentOfAction = (
    action: Exclude<LibraryRowAction, "rename">,
    ref: ResourceRef,
): LibraryRowIntent => {
    switch (action) {
        case "archive":
            return { type: "archive-resource", ref };
        case "unarchive":
            return { type: "unarchive-resource", ref };
        case "delete":
            return { type: "request-delete-resource", ref };
    }
};
